/**
 * Game Settings Constants
 * Centralizes game configuration values that were previously hardcoded
 */

// Camera settings
export const CAMERA_SETTINGS = {
  HEIGHT: 10,            // Default camera height above the bus
  DISTANCE: 30,          // Default camera distance behind the bus
  SMOOTH_FACTOR: 0.1,    // Camera smoothing factor (lower = smoother)
  FOV: 75,               // Field of view for main camera
  NEAR_PLANE: 0.1,       // Near clipping plane
  FAR_PLANE: 1000        // Far clipping plane
};

// Environment settings
export const ENVIRONMENT = {
  TERRAIN_SIZE: 2000,
  FOG_COLOR: 0xCCE8FF,
  FOG_DENSITY: 0.0015,
  BACKGROUND_COLOR: 0xCCE8FF
};

// Coin system settings
export const COIN_SYSTEM = {
  COUNT: 50,            // Number of coins to spawn in the world
  SPAWN_RADIUS: 150,    // Radius to spawn coins within
  MIN_DISTANCE: 10,     // Minimum distance between coins
  REFUEL_COST: 1        // Number of coins needed to refuel
};

// Gas station settings
export const GAS_STATIONS = {
  COUNT: 5,             // Number of gas stations to create
  MIN_DISTANCE: 200     // Minimum distance between stations
};

// Pyramid settings
export const PYRAMIDS = {
  MIN_COUNT: 3,         // Minimum number of pyramids to create
  MAX_COUNT: 5,         // Maximum number of pyramids to create
  MIN_DISTANCE: 300,    // Minimum distance between pyramids
  MIN_WIDTH: 50,        // Minimum width of pyramids
  MAX_WIDTH: 120,        // Maximum width of pyramids
  MIN_HEIGHT: 40,       // Minimum height of pyramids
  MAX_HEIGHT: 70,       // Maximum height of pyramids
  EXCLUSION_RADIUS: 50  // Radius to keep pyramids away from other important objects
};

// Collision settings
export const COLLISION = {
  ENABLED: true,               // Enable collision effects
  SOUND_ENABLED: true,         // Enable collision sounds
  VISUAL_ENABLED: true,        // Enable visual effects on collision
  MIN_IMPACT_FOR_SOUND: 20,    // Minimum impact force for sound
  MIN_IMPACT_FOR_VISUAL: 30,   // Minimum impact force for visual effect
  SHAKE_DURATION: 0.5,         // Duration of camera shake in seconds
  SHAKE_INTENSITY: 0.3,        // Maximum intensity of camera shake
  COOLDOWN: 300               // Milliseconds between allowed collision sounds
};

// Minimap settings
export const MINIMAP = {
  SIZE: 120,                    // Size of minimap in pixels
  CAMERA: {
    VIEW_DISTANCE: 150,         // Orthographic camera view distance
    HEIGHT: 300,                // Camera height above the sce
  },
};

// Smoke settings
export const SMOKE = {
  ENABLED: false,
  GLOBAL_DENSITY: 1.0,
  COLOR: '#e0e8ff'      // Brighter blue-white color for better visibility
};

// Renderer settings
export const RENDERER = {
  SHADOWS_ENABLED: true,
  SHADOW_TYPE: 'PCFSoft' // Options: 'Basic', 'PCF', 'PCFSoft'
};

// Bus settings
export const BUS = {
  HIGH_SPEED_THRESHOLD: 12.5 // 45km/h
}

// Weapon settings
export const WEAPONS = {
  machineGun: {
    damage: 10,
    fireRate: 10,
    projectileSpeed: 500, // Increased to make projectiles more visible and impactful
    ammo: 100,
    reloadTime: 3,
    range: 500, // longer range possible
    spread: 0.01,
    projectileType: 'laser',
    projectileSize: 0.2,
    soundId: 'laser'
  },
  laserCannon: {
    name: 'Laser Cannon',
    damage: 25,          // Maybe lower damage, but continuous? Or higher burst.
    fireRate: 8,          // How fast it pulses
    projectileSpeed: 400, // Very fast 'pulse' speed
    projectileSize: 0.15, // Radius of the laser cylinder/beam
    ammo: 200,           // Energy based?
    reloadTime: 3.0,
    range: 400,
    spread: 0.5,
    projectileType: 'laser' // Set type to laser
  }
}

// Audio settings
export const AUDIO_PATHS = {
  ENGINE_IDLE: '/audio/engine_idle.mp3',
  ENGINE_ACCEL: '/audio/engine_accelerate.mp3',
  ENGINE_STEADY: '/audio/engine_steady.mp3',
  BRAKING: '/audio/braking.mp3',
  HORN: '/audio/horn.mp3',
  COLLISION_SOFT: '/audio/collision_soft.mp3',
  COLLISION_MEDIUM: '/audio/collision_medium.mp3',
  COLLISION_HARD: '/audio/collision_hard.mp3',
  COIN_COLLECTED: '/audio/coin_collected.mp3',
  WEAPON_MACHINE_GUN: '/audio/machine_gun.mp3',
  WEAPON_ROCKET_LAUNCH: '/audio/rocket_launch.mp3',
  WEAPON_EXPLOSION: '/audio/explosion.mp3',
  WEAPON_OIL_SLICK: '/audio/oil_slick.mp3'
};

export const SOUND_IDS = {
  ENGINE_IDLE: 'engine_idle',
  ENGINE_ACCEL: 'engine_accelerate',
  ENGINE_STEADY: 'engine_steady',
  BRAKING: 'braking',
  HORN: 'horn',
  COLLISION_SOFT: 'collision_soft',
  COLLISION_MEDIUM: 'collision_medium',
  COLLISION_HARD: 'collision_hard',
  COIN_COLLECTED: 'coin_collected',
  WEAPON_MACHINE_GUN: 'weapon_machine_gun',
  WEAPON_ROCKET_LAUNCH: 'weapon_rocket_launch',
  WEAPON_EXPLOSION: 'weapon_explosion',
  WEAPON_OIL_SLICK: 'weapon_oil_slick',
  WEAPON_LASER: 'laser'
};

export const AUDIO_DEFAULTS = {
  ENGINE_IDLE_VOL: 0.3,
  ENGINE_ACCEL_VOL: 0.3,
  ENGINE_STEADY_VOL: 0.4,
  BRAKING_VOL: 0.4,
  HORN_VOL: 0.8,
  COLLISION_SOFT_VOL: 0.7,
  COLLISION_MEDIUM_VOL: 0.8,
  COLLISION_HARD_VOL: 1.0,
  COIN_COLLECTED_VOL: 0.5,
  WEAPON_MACHINE_GUN_VOL: 0.6,
  WEAPON_ROCKET_LAUNCH_VOL: 0.7,
  WEAPON_EXPLOSION_VOL: 0.8,
  WEAPON_OIL_SLICK_VOL: 0.5,

  REF_DISTANCE: 20,
  ROLLOFF_FACTOR: 1,
  DISTANCE_MODEL: 'linear',
  MASTER_VOLUME: 1.0,
};

export const ENGINE_SOUND_PARAMS = {
  STEADY_PITCH_MIN: 1.0,
  STEADY_PITCH_MAX: 1.4,
  STEADY_SPEED_DIVISOR: 30,
  IDLE_PITCH_MIN: 0.8,
  IDLE_PITCH_MAX: 1.6,
  IDLE_SPEED_DIVISOR: 40,
  ACCEL_THRESHOLD: 0.6,
  ACCEL_MAX_SPEED: 30,
};

export const COLLISION_SOUND_PARAMS = {
  MEDIUM_IMPACT_THRESHOLD: 50,
  HARD_IMPACT_THRESHOLD: 150,
  MAX_FORCE_FOR_VOLUME_SCALING: 200,
  VOLUME_BASE_SCALING: 0.7,
  VOLUME_FORCE_SCALING: 0.3,
};

export const SOUND_CLEANUP_DELAY_MS = 100;

// Challenge system settings
export const CHALLENGES = {
  ENABLED: true,
  INITIAL_DELAY: 10,          // Seconds before first challenge
  TIME_BETWEEN: 15,           // Seconds between challenges
  CHECKPOINT_SPAWN_RADIUS: 120,
  CHECKPOINT_MIN_DISTANCE: 40,
};

// Score system settings
export const SCORING = {
  COIN_COLLECTED: 100,
  CHECKPOINT_REACHED: 500,
  CHALLENGE_COMPLETED: 2000,
  METEOR_SURVIVED: 150,
  SPEED_BONUS: 10,
  SPEED_BONUS_THRESHOLD: 50,  // m/s (~180 km/h)
  COMBO_TIMEOUT: 5,           // Seconds before combo resets
  MAX_MULTIPLIER: 8,
};